import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import {
  Smartphone, CheckCircle, XCircle, Send, Clock, BookOpen,
  RefreshCw, Trash2, Plus, MessageSquare, Users, AlertCircle,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import WhatsAppQRConnect from '../../components/admin/WhatsAppQRConnect';
import WhatsAppSendModal from '../../components/admin/WhatsAppSendModal';
import { GET_WHATSAPP_ACCOUNT, GET_WHATSAPP_MESSAGES, GET_WHATSAPP_TEMPLATES } from '../../graphql/queries/whatsapp';
import {
  DISCONNECT_WHATSAPP,
  CREATE_WHATSAPP_TEMPLATE,
  UPDATE_WHATSAPP_TEMPLATE,
  DELETE_WHATSAPP_TEMPLATE,
} from '../../graphql/mutations/whatsapp';
import { useAuth } from '../../lib/auth';
import type { WhatsAppAccount, WhatsAppMessage, WhatsAppTemplate, WhatsAppTemplateInput } from '../../types/whatsapp';

type Tab = 'overview' | 'history' | 'templates';

const STATUS_STYLES: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  PENDING:   { label: 'En attente',  className: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-3 h-3" /> },
  SENT:      { label: 'Envoyé',      className: 'bg-blue-100 text-blue-700',    icon: <Send className="w-3 h-3" /> },
  DELIVERED: { label: 'Délivré',     className: 'bg-indigo-100 text-indigo-700', icon: <CheckCircle className="w-3 h-3" /> },
  READ:      { label: 'Lu',          className: 'bg-green-100 text-green-700',  icon: <CheckCircle className="w-3 h-3" /> },
  FAILED:    { label: 'Échoué',      className: 'bg-red-100 text-red-700',      icon: <XCircle className="w-3 h-3" /> },
};

const emptyTemplate: WhatsAppTemplateInput = { name: '', content: '', language: 'fr', variables: [], isDefault: false };

export default function WhatsAppPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showQRPanel, setShowQRPanel] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [templateForm, setTemplateForm] = useState<WhatsAppTemplateInput>(emptyTemplate);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [oauthError, setOauthError] = useState('');

  // Détection retour OAuth Meta
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      const messages: Record<string, string> = {
        oauth_no_code: 'Connexion OAuth annulée',
        no_phone_number: 'Aucun numéro WhatsApp trouvé sur ce compte Meta',
        oauth_failed: 'Erreur lors de la connexion OAuth Meta',
      };
      setOauthError(messages[error] || 'Erreur de connexion');
    }
  }, [searchParams]);

  const { data: accountData, loading: accountLoading, refetch: refetchAccount } = useQuery(GET_WHATSAPP_ACCOUNT, {
    fetchPolicy: 'network-only',
    skip: !user,
  });

  const { data: messagesData, loading: messagesLoading, refetch: refetchMessages } = useQuery(GET_WHATSAPP_MESSAGES, {
    variables: { limit: 50, offset: 0 },
    fetchPolicy: 'network-only',
    skip: !user || activeTab !== 'history',
  });

  const { data: templatesData, refetch: refetchTemplates } = useQuery(GET_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'network-only',
    skip: !user || activeTab !== 'templates',
  });

  const [disconnectWhatsApp, { loading: disconnecting }] = useMutation(DISCONNECT_WHATSAPP, {
    onCompleted: () => refetchAccount(),
    onError: (err) => alert(err.message),
  });

  const [createTemplate, { loading: creatingTemplate }] = useMutation(CREATE_WHATSAPP_TEMPLATE, {
    onCompleted: () => { refetchTemplates(); setShowTemplateForm(false); setTemplateForm(emptyTemplate); },
    onError: (err) => alert(err.message),
  });

  const [updateTemplate, { loading: updatingTemplate }] = useMutation(UPDATE_WHATSAPP_TEMPLATE, {
    onCompleted: () => { refetchTemplates(); setShowTemplateForm(false); setEditingTemplate(null); setTemplateForm(emptyTemplate); },
    onError: (err) => alert(err.message),
  });

  const [deleteTemplate] = useMutation(DELETE_WHATSAPP_TEMPLATE, {
    onCompleted: () => refetchTemplates(),
    onError: (err) => alert(err.message),
  });

  const account: WhatsAppAccount | null = accountData?.whatsAppAccount ?? null;
  const messages: WhatsAppMessage[] = messagesData?.whatsAppMessages ?? [];
  const messagesCount: number = messagesData?.whatsAppMessagesCount ?? 0;
  const templates: WhatsAppTemplate[] = templatesData?.whatsAppTemplates ?? account?.templates ?? [];

  const startEditTemplate = (t: WhatsAppTemplate) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, content: t.content, language: t.language, variables: t.variables, isDefault: t.isDefault });
    setShowTemplateForm(true);
  };

  const handleTemplateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateTemplate({ variables: { id: editingTemplate.id, input: templateForm } });
    } else {
      createTemplate({ variables: { input: templateForm } });
    }
  };

  if (!user) return null;

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview',   label: 'Vue d\'ensemble', icon: <Smartphone className="w-4 h-4" /> },
    { key: 'history',    label: `Historique${account ? ` (${account.messagesCount})` : ''}`, icon: <Clock className="w-4 h-4" /> },
    { key: 'templates',  label: 'Templates',       icon: <BookOpen className="w-4 h-4" /> },
  ];

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              WhatsApp Business
            </h1>
            <p className="text-sm text-gray-500 mt-1">Envoyez vos offres de voyage directement sur WhatsApp</p>
          </div>
          {account?.isActive && (
            <button
              onClick={() => setShowSendModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            >
              <Send className="w-4 h-4" /> Envoyer une offre
            </button>
          )}
        </div>

        {/* OAuth error */}
        {oauthError && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {oauthError}
            <button onClick={() => setOauthError('')} className="ml-auto text-red-400 hover:text-red-600"><XCircle className="w-4 h-4" /></button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ── Vue d'ensemble ─────────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {accountLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : account?.isActive ? (
              <>
                {/* Compte connecté */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                        <Smartphone className="w-7 h-7 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{account.displayName || 'WhatsApp Business'}</h3>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Connecté
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm mt-0.5">{account.phoneNumber}</p>
                        <p className="text-gray-400 text-xs mt-0.5">ID: {account.phoneNumberId}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { if (confirm('Déconnecter WhatsApp ? Vous devrez rescanner le QR.')) disconnectWhatsApp(); }}
                      disabled={disconnecting}
                      className="flex items-center gap-1 px-3 py-1.5 border border-red-200 rounded-lg text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Déconnecter
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Messages envoyés', value: account.messagesCount, icon: <Send className="w-5 h-5 text-blue-500" />, color: 'blue' },
                    { label: 'Templates', value: templates.length, icon: <BookOpen className="w-5 h-5 text-purple-500" />, color: 'purple' },
                    { label: 'Statut webhook', value: account.webhookVerified ? 'Vérifié' : 'Non vérifié', icon: <CheckCircle className="w-5 h-5 text-green-500" />, color: 'green' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-${stat.color}-50 rounded-lg flex items-center justify-center`}>
                          {stat.icon}
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                          <p className="text-xs text-gray-500">{stat.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Webhook config */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Configuration Webhook Meta</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">URL Webhook : </span>
                      <code className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs">
                        {window.location.protocol}//{window.location.hostname.replace('5173', '4000')}/api/whatsapp/webhook
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-500">Verify Token : </span>
                      <code className="text-gray-700 bg-gray-100 px-2 py-0.5 rounded text-xs">
                        ziyara-whatsapp-verify
                      </code>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Non connecté */
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-green-500" /> Connecter WhatsApp
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Scannez le QR code avec votre téléphone — comme WhatsApp Web. Aucun compte Meta requis.
                  </p>
                </div>
                <div className="p-6">
                  <WhatsAppQRConnect
                    onConnected={(phone, displayName) => {
                      refetchAccount();
                      setShowQRPanel(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Historique ─────────────────────────────────────────────────────── */}
        {activeTab === 'history' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{messagesCount} message{messagesCount !== 1 ? 's' : ''}</h3>
              <button onClick={() => refetchMessages()} className="text-gray-400 hover:text-gray-600">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {messagesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">Aucun message envoyé</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {messages.map(msg => {
                  const statusInfo = STATUS_STYLES[msg.status] || STATUS_STYLES.PENDING;
                  return (
                    <div key={msg.id} className="flex items-start gap-4 p-4 hover:bg-gray-50">
                      <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-gray-900">{msg.recipientName || msg.recipientPhone}</span>
                          {msg.recipientName && <span className="text-xs text-gray-400">{msg.recipientPhone}</span>}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                            {statusInfo.icon} {statusInfo.label}
                          </span>
                        </div>
                        {msg.package && (
                          <p className="text-xs text-gray-500 mt-0.5">Package : {msg.package.title}</p>
                        )}
                        {msg.errorMessage && (
                          <p className="text-xs text-red-500 mt-0.5">{msg.errorMessage}</p>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-400 flex-shrink-0">
                        <p>{new Date(msg.createdAt).toLocaleDateString('fr-FR')}</p>
                        <p>{new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                        {msg.readAt && <p className="text-green-500">Lu</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Templates ──────────────────────────────────────────────────────── */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => { setEditingTemplate(null); setTemplateForm(emptyTemplate); setShowTemplateForm(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
              >
                <Plus className="w-4 h-4" /> Nouveau template
              </button>
            </div>

            {showTemplateForm && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
                </h3>
                <form onSubmit={handleTemplateSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom <span className="text-red-500">*</span></label>
                      <input
                        value={templateForm.name}
                        onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="ex: Offre standard"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Langue</label>
                      <select
                        value={templateForm.language}
                        onChange={e => setTemplateForm(p => ({ ...p, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="fr">Français</option>
                        <option value="ar">Arabe</option>
                        <option value="en">Anglais</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contenu <span className="text-red-500">*</span>
                      <span className="text-gray-400 font-normal ml-1">(utilisez {'{{variable}}'} pour les variables)</span>
                    </label>
                    <textarea
                      value={templateForm.content}
                      onChange={e => setTemplateForm(p => ({ ...p, content: e.target.value }))}
                      placeholder="✈️ Bonjour {{nom}}, découvrez notre offre pour {{destination}}..."
                      rows={5}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isDefault"
                      checked={templateForm.isDefault}
                      onChange={e => setTemplateForm(p => ({ ...p, isDefault: e.target.checked }))}
                      className="h-4 w-4 text-green-600 border-gray-300 rounded"
                    />
                    <label htmlFor="isDefault" className="text-sm text-gray-700">Template par défaut</label>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => { setShowTemplateForm(false); setEditingTemplate(null); }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                      Annuler
                    </button>
                    <button type="submit" disabled={creatingTemplate || updatingTemplate}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                      {editingTemplate ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {templates.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-400 text-sm">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                Aucun template. Créez-en un pour personnaliser vos messages.
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(t => (
                  <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-4">
                    <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">{t.name}</span>
                        <span className="text-xs text-gray-400">{t.language}</span>
                        {t.isDefault && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Défaut</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2">{t.content}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEditTemplate(t)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => { if (confirm('Supprimer ce template ?')) deleteTemplate({ variables: { id: t.id } }); }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <WhatsAppSendModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        templates={templates}
        onSent={() => { refetchAccount(); if (activeTab === 'history') refetchMessages(); }}
      />
    </AdminLayout>
  );
}
